import React, { useEffect, useRef, useState } from 'react';
import {
  getNode,
  setNodes,
} from '@udecode/plate-core';
import classNames from 'classnames';
import { Edit, Trash } from '@styled-icons/feather';
import { throttle } from 'lodash';
import { Path } from 'slate';
import styles from '../../SideToolbar.module.css';
import { Dropdown, DropdownItem } from '../../../../components/Dropdown';
import { useTPlateEditorRef } from '../../../../plate';
import { DreamEditor } from '../../../../types';
import { getSelectedElementEntry } from '../../../../plugins/elementSelection';
import { useDreamEditorStore } from '../../../../plugins/store';
import ToolbarCustomAttributesInput from './ToolbarCustomAttributesInput';
import { setBlockProperty } from '../../../../editorTools/transforms';

function InputAttribute() {
  const [isDisabled, setIsDisabled] = useState(true);
  const editor = useTPlateEditorRef();

  const [path, selection] = useDreamEditorStore(
    (state) => [getSelectedElementEntry(editor)?.[1] as Path, state.editor?.selection],
  );

  const optionAttributes = ['id', 'class', 'data-analytics'];
  const inputRef = useRef(null);

  const [selectedAttribute, setSelectedAttribute] = useState('');
  const [attributeValue, setAttributeValue] = useState<any>(null);
  const [value, setValue] = useState('');
  const [customKeyOpen, setCustomKeyOpen] = useState(false);
  const [editingWordIndex, setEditingWordIndex] = useState<number | undefined>(undefined);

  useEffect(() => setIsDisabled(!path), [path]);

  useEffect(() => {
    if (selection || path) {
      try {
        const el = getNode(editor, path) as DreamEditor;
        setAttributeValue(el.customAttributes as any ?? {});
        setSelectedAttribute('');
        setValue('');
      } catch (e) {
        // do nothing
      }
    }
  }, [editor, selection, path]);

  const onSelectAttribute = (key: string) => {
    setSelectedAttribute(key);
    setValue(attributeValue?.[`${key}`] ?? '');
  };

  const onChange = throttle((event) => {
    if (!path) return;

    const eTValue = event.target.value;
    const newValue = attributeValue ?? {};
    const attributeName = selectedAttribute === 'class' ? 'className' : selectedAttribute;
    (newValue as any)[`${attributeName}`] = attributeName === 'id' ? eTValue.replace(' ', '-') : eTValue;
    setValue(eTValue);
    setAttributeValue(newValue);
    setNodes(
      editor,
      {
        customAttributes: attributeValue,
      },
      { at: path },
    );
  }, 500);

  const handleRemoveAttributes = (key: string, index: number
  | React.SetStateAction<undefined | number>) => {
    if (!path) return;
    setEditingWordIndex(index);
    const newValue = attributeValue;
    delete newValue[`${key}`];
    setAttributeValue((prev: any) => {
      const newPrev = { ...prev };
      delete newPrev[`${key}`];
      return newPrev;
    });
    setBlockProperty(editor, 'customAttributes', newValue, { at: path });
    setEditingWordIndex(undefined);
  };

  return (
    <div className={styles.section}>
      { /* eslint-disable-next-line jsx-a11y/label-has-associated-control */ }
      <label htmlFor="#" className={styles.label}>Attributes</label>
      <div className={styles.menuGroup}>
        <div className={classNames(styles.flex1)}>
          <Dropdown
            dropDownMenuStyle={{ width: '115%' }} // TODO@bayu: Re-implement using floating-ui
            triggerLabel={(
              <span
                data-testid="Toolbar_attribute_trigger"
                className={classNames({ 'pt-1': selectedAttribute.length, tag: selectedAttribute.length })}
                style={{
                  backgroundColor: 'var(--border-modal-transparent)', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: 80, display: 'inline-block',
                }}
              >
                {selectedAttribute || 'key'}
              </span>
            )}
            disabled={isDisabled}
          >
            {optionAttributes.map((o) => (
              <DropdownItem key={o}>
                <div
                  onClick={() => { onSelectAttribute(o); }}
                  role="none"
                  data-testid={`Toolbar_attributes_${o}`}
                  className={styles
                    .dropdownItemAttribute}
                >
                  <span className="tag" style={{ backgroundColor: 'var(--border-modal-transparent)' }}>
                    {o}
                  </span>
                </div>
              </DropdownItem>
            ))}
            <ToolbarCustomAttributesInput
              selectedAttribute={selectedAttribute}
              customKeyOpen={customKeyOpen}
              setCustomKeyOpen={setCustomKeyOpen}
              setSelectedAttribute={setSelectedAttribute}
            />
          </Dropdown>
        </div>
        <input
          className={classNames(styles.menuItem, styles.flex1)}
          ref={inputRef}
          type="text"
          value={value}
          placeholder="Insert value"
          onChange={onChange}
          disabled={!selectedAttribute.length}
          data-testid="Toolbar_InsertLink_Input"
        />
      </div>

      {
             !!attributeValue && Object.keys(attributeValue).map((item, index) => (

               editingWordIndex === index
                 ? (
                   <div className={classNames(styles.menuGroup, 'mb-1')}>
                     <input
                       className={classNames(styles.menuItem, styles.flex1)}
                       type="text"
                       value={attributeValue[item]}
                       placeholder="Paste link or search page"
                       onChange={(e) => setAttributeValue((prev: any) => {
                         const newPrev = { ...prev };
                         newPrev[item] = e.target.value;
                         return newPrev;
                       })}
                       data-testid="Toolbar_LeafAnimation_Input"
                     />

                     <button
                       className={classNames(styles.menuItem)}
                       type="button"
                       onClick={() => {
                         setNodes(
                           editor,
                           {
                             customAttributes: attributeValue,
                           },
                           { at: path },
                         );
                         setEditingWordIndex(undefined);
                       }}
                       data-testid="Toolbar_InsertLink_Save"
                     >
                       <div className="p-1">
                         Save
                       </div>
                     </button>
                   </div>
                 )
                 : (
                   <div className={classNames(styles.card, 'mb-1')}>
                     <div className="is-flex is-align-items-center" style={{ gap: 0 }}>
                       <div className={classNames('with-text-color has-text-weight-bold', styles.flex1)}>
                         {item}
                         <div className={classNames('with-text-color has-text-weight-light')}>
                           {attributeValue[item]}
                         </div>
                       </div>
                       <div className="is-flex is-align-items-center">

                         <button
                           className={styles.icon}
                           type="button"
                           onClick={() => { setEditingWordIndex(index); }}
                         >
                           <Edit
                             size={16}
                           />
                         </button>

                         <button
                           className={styles.icon}
                           type="button"
                           onClick={() => handleRemoveAttributes(item, index)}
                         >
                           <Trash size={18} />
                         </button>
                       </div>
                     </div>
                   </div>
                 )
             ))
            }
    </div>
  );
}

export default InputAttribute;
